import { useState, useEffect } from 'react';
import { Plane, Sparkles } from 'lucide-react';

interface LuggageCompletionAnimationProps {
  onComplete: () => void;
}

export function LuggageCompletionAnimation({ onComplete }: LuggageCompletionAnimationProps) {
  const [animationPhase, setAnimationPhase] = useState<'preparing' | 'zipping' | 'moving' | 'complete'>('preparing');

  useEffect(() => {
    const timers: NodeJS.Timeout[] = [];

    // Phase 1: Preparing (0.5 seconds)
    timers.push(setTimeout(() => {
      setAnimationPhase('zipping');
    }, 500));

    // Phase 2: Zipping animation (2.5 seconds)
    timers.push(setTimeout(() => {
      setAnimationPhase('moving');
    }, 3000));

    // Phase 3: Moving animation (2.5 seconds)
    timers.push(setTimeout(() => {
      setAnimationPhase('complete');
      onComplete();
    }, 5500));

    return () => {
      timers.forEach(timer => clearTimeout(timer));
    };
  }, [onComplete]);

  return (
    <div className="relative w-full max-w-md mx-auto mb-6 p-6 bg-gradient-to-br from-background via-background to-primary/5 border border-border/50 rounded-2xl shadow-xl backdrop-blur-sm">
      <div className="relative w-full mx-auto">
        {/* Sparkle Effects */}
        <div className="absolute inset-0 overflow-hidden pointer-events-none">
          {[...Array(6)].map((_, i) => (
            <Sparkles
              key={i}
              className={`absolute text-primary/30 animate-pulse ${
                i % 2 === 0 ? 'animate-bounce' : ''
              }`}
              size={16 + (i % 3) * 8}
              style={{
                left: `${20 + (i * 15)}%`,
                top: `${10 + (i % 4) * 20}%`,
                animationDelay: `${i * 0.3}s`,
                animationDuration: `${2 + (i % 3)}s`
              }}
            />
          ))}
        </div>

        {/* Main Animation Container */}
        <div className="relative h-40 flex items-center justify-center overflow-hidden">
          {/* Luggage Container */}
          <div 
            className={`
              relative flex items-center justify-center transition-all ease-in-out
              ${animationPhase === 'preparing' ? 'scale-95 opacity-80' : ''}
              ${animationPhase === 'zipping' ? 'scale-105 opacity-100 duration-500' : ''}
              ${animationPhase === 'moving' ? 'translate-x-[200px] scale-90 opacity-80 duration-[2000ms]' : ''}
            `}
          >
            {/* Luggage Shadow */}
            <div className={`
              absolute bottom-0 w-20 h-3 bg-primary/20 rounded-full blur-sm transform translate-y-6
              ${animationPhase === 'moving' ? 'scale-x-150' : ''}
              transition-all duration-1000
            `}></div>

            {/* Main Luggage Body */}
            <div 
              className={`
                relative w-20 h-16 bg-gradient-to-br from-primary to-primary/80 rounded-xl shadow-xl
                ${animationPhase === 'zipping' ? 'animate-pulse' : ''}
                transition-all duration-300
              `}
            >
              {/* Luggage Details */}
              <div className="absolute inset-1 border border-primary-foreground/20 rounded-lg"></div>
              
              {/* Handle */}
              <div className="absolute -top-2 left-1/2 transform -translate-x-1/2 w-8 h-4 border-2 border-primary bg-transparent rounded-t-xl"></div>
              
              {/* Zipper */}
              <div 
                className={`
                  absolute top-1/2 left-1 h-0.5 bg-primary-foreground/90 rounded-full transition-all duration-2000 ease-in-out
                  ${animationPhase === 'zipping' || animationPhase === 'moving' ? 'w-18' : 'w-0'}
                `}
              ></div>
              
              {/* Zipper Pull */}
              <div 
                className={`
                  absolute top-1/2 w-1.5 h-1.5 bg-primary-foreground rounded-full transition-all duration-2000 ease-in-out transform -translate-y-1/2
                  ${animationPhase === 'zipping' || animationPhase === 'moving' ? 'left-18' : 'left-1'}
                `}
              ></div>
              
              {/* Wheels */}
              <div className="absolute -bottom-1 left-2 w-3 h-3 bg-primary-foreground/80 rounded-full shadow-md"></div>
              <div className="absolute -bottom-1 right-2 w-3 h-3 bg-primary-foreground/80 rounded-full shadow-md"></div>
              
              {/* Luggage Tag */}
              <div className="absolute top-1 right-1 w-4 h-3 bg-accent rounded-sm opacity-80"></div>
            </div>

            {/* Motion Lines (when moving) */}
            {animationPhase === 'moving' && (
              <div className="absolute right-full top-1/2 transform -translate-y-1/2 flex space-x-1">
                {[...Array(3)].map((_, i) => (
                  <div
                    key={i}
                    className="w-4 h-0.5 bg-primary/40 rounded-full animate-pulse"
                    style={{
                      animationDelay: `${i * 0.1}s`,
                      opacity: 1 - (i * 0.2)
                    }}
                  ></div>
                ))}
              </div>
            )}
          </div>

          {/* "Enjoy your trip" message */}
          {animationPhase === 'moving' && (
            <div className="absolute inset-0 flex flex-col items-center justify-center pointer-events-none">
              <div className="text-2xl font-bold bg-gradient-to-r from-primary to-accent bg-clip-text text-transparent animate-fade-in mb-1">
                Enjoy your trip!
              </div>
              <Plane className="w-6 h-6 text-primary animate-bounce" />
            </div>
          )}
        </div>

        {/* Status Text */}
        <div className="text-center space-y-4 mt-8">
          {animationPhase === 'preparing' && (
            <div className="animate-fade-in">
              <h3 className="text-xl font-bold text-foreground">Getting ready...</h3>
              <p className="text-muted-foreground">Preparing your luggage</p>
            </div>
          )}
          
          {animationPhase === 'zipping' && (
            <div className="animate-fade-in">
              <h3 className="text-xl font-bold text-foreground">Zipping up...</h3>
              <p className="text-muted-foreground">Securing all your essentials</p>
            </div>
          )}
          
          {animationPhase === 'moving' && (
            <div className="animate-fade-in">
              <h3 className="text-xl font-bold text-foreground">All packed!</h3>
              <p className="text-muted-foreground">Ready for your adventure</p>
            </div>
          )}

          {/* Progress Animation */}
          {(animationPhase === 'preparing' || animationPhase === 'zipping') && (
            <div className="flex justify-center space-x-2 mt-4">
              {[...Array(3)].map((_, i) => (
                <div
                  key={i}
                  className="w-3 h-3 bg-primary rounded-full animate-bounce"
                  style={{
                    animationDelay: `${i * 0.2}s`,
                    animationDuration: '1s'
                  }}
                ></div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}